import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Truck, CheckCircle2, XCircle, Clock, Printer, Receipt, Copy, FileText, Edit3, Plus, Minus, Trash2 } from "lucide-react";
import api from "../lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/Card";
import { TableRowsSkeleton } from "../components/ui/Skeleton";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { Textarea, Label, Select, Input } from "../components/ui/Input";
import { toast } from "../components/ui/Toast";
import PrintPreview from "../components/ui/PrintPreview";
import { BillThermal, PhieuXuatKho } from "../components/print/BillTemplates";
import Pin2Prompt from "../components/ui/Pin2Prompt";
import { useI18n } from "../contexts/I18nContext";
import { useFeature } from "../contexts/CreatorConfigContext";
import { formatVND, formatDateTime, parseApiError} from "../lib/utils";

const STATUS_ICONS = {
  new: Clock,
  processing: Clock,
  preparing: Clock,
  delivering: Truck,
  delivered: CheckCircle2,
  debt_pending: Clock,
  cancelled: XCircle,
};

const NEXT_STATUS = {
  new: ["processing", "delivering", "cancelled"],
  processing: ["delivering", "cancelled"],
  preparing: ["delivering", "cancelled"],
  delivering: ["delivered", "debt_pending", "cancelled"],
  delivered: [],
  debt_pending: ["delivered", "cancelled"],
  cancelled: [],
};

export default function OrderDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const canDuplicate  = useFeature("orders.duplicate");
  const canBillPrint  = useFeature("orders.bill_print");
  const canDeleteOrder = useFeature("orders.delete");
  const [order, setOrder] = useState(null);
  const quantityRefs = useRef({});
  const [shop, setShop] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusNote, setStatusNote] = useState("");
  const [printMode, setPrintMode] = useState(null); // "80mm" | "a4" | "a5"
  const [pin2Open, setPin2Open]               = useState(false);
  const [pin2RetryAction, setPin2RetryAction] = useState(null);
  const [editOpen, setEditOpen]               = useState(false);
  const [editForm, setEditForm]               = useState(null);   // copy của order đang sửa
  const [products, setProducts]               = useState([]);
  const [saving, setSaving]                   = useState(false); // function to retry on success

  const load = async () => {
    setLoading(true);
    try {
      const [o, s] = await Promise.all([
        api.get(`/orders/${id}`),
        api.get(`/settings`),
      ]);
      const orderData = o.data;

      // Nếu order thiếu thông tin KH (đơn cũ), enrich từ customer
      if (orderData.customer_id && (!orderData.customer_email && !orderData.customer_cccd && !orderData.customer_tax_id)) {
        try {
          const customers = await api.get("/customers");
          const cust = (customers.data || []).find(c => c.customer_id === orderData.customer_id);
          if (cust) {
            if (!orderData.customer_email) orderData.customer_email = cust.email || "";
            if (!orderData.customer_cccd) orderData.customer_cccd = cust.tax_id || "";
            if (!orderData.customer_tax_id) orderData.customer_tax_id = cust.tax_id || "";
          }
        } catch (_) {}
      }

      setOrder(orderData);
      setShop(s.data || {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Tự mở modal chỉnh sửa nếu navigate từ danh sách với state openEdit
  useEffect(() => {
    if (location.state?.openEdit && order && !editOpen) {
      openEdit();
    }
  // eslint-disable-next-line
  }, [order, location.state]);

  const openEdit = async () => {
    if (!products.length) {
      const r = await api.get("/products");
      setProducts(r.data || []);
    }
    setEditForm({
      items: (order.items || []).map(i => ({ ...i })),
      customer_name: order.customer_name || "",
      customer_phone: order.customer_phone || "",
      customer_address: order.customer_address || "",
      payment_method: order.payment_method || "cash",
      discount_percent: order.discount_percent || 0,
      discount: order.discount || 0,
      shipping_fee: order.shipping_fee || 0,
      note: order.note || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    const doSave = async () => {
      setSaving(true);
      try {
        const payload = {
          customer_name: editForm.customer_name,
          customer_phone: editForm.customer_phone,
          customer_address: editForm.customer_address,
          payment_method: editForm.payment_method,
          discount_percent: Number(editForm.discount_percent) || 0,
          discount: Number(editForm.discount) || 0,
          shipping_fee: Number(editForm.shipping_fee) || 0,
          note: editForm.note,
          items: editForm.items.map(i => ({
            product_id: i.product_id,
            quantity: Number(i.quantity),
            price: Number(i.price),
          })),
        };
        const { data } = await api.put(`/orders/${id}`, payload);
        setOrder(data);
        setEditOpen(false);
        toast({ title: "Đã cập nhật đơn hàng ✓", variant: "success" });
      } catch (e) {
        if (e?.response?.status === 403 && e?.response?.data?.detail === "PIN2_REQUIRED") {
          setPin2RetryAction(() => doSave);
          setPin2Open(true);
          return;
        }
        const detail = parseApiError(e);
        let msg = "Lỗi cập nhật";
        if (typeof detail === "string") msg = detail;
        else if (Array.isArray(detail)) msg = detail.map(d => d.msg || JSON.stringify(d)).join(", ");
        else if (detail) msg = JSON.stringify(detail);
        toast({ title: msg, variant: "error" });
      } finally { setSaving(false); }
    };
    await doSave();
  };

  const editSubtotal = editForm
    ? editForm.items.reduce((s, i) => s + Number(i.price) * Number(i.quantity), 0)
    : 0;
  const editTotal = editForm
    ? editSubtotal
      - Math.round(editSubtotal * (Number(editForm.discount_percent) || 0) / 100)
      - (Number(editForm.discount) || 0)
      + (Number(editForm.shipping_fee) || 0)
    : 0;

  const duplicate = async () => {
    navigate(`/orders/new?clone_from=${id}`);
  };

  const updateStatus = async (newStatus) => {
    const doUpdate = async () => {
      try {
        const { data } = await api.put(`/orders/${id}/status`, { status: newStatus, note: statusNote });
        setOrder(data);
        setStatusNote("");
        toast({ title: t("common.update") + " ✓", variant: "success" });
      } catch (e) {
        if (e?.response?.status === 403 && e?.response?.data?.detail === "PIN2_REQUIRED") {
          setPin2RetryAction(() => doUpdate);
          setPin2Open(true);
          return;
        }
        toast({ title: parseApiError(e) || t("common.error"), variant: "error" });
      }
    };
    await doUpdate();
  };

  const removeOrder = async () => {
    if (!window.confirm(`Xóa đơn ${order.order_code}? Tồn kho sẽ được khôi phục.`)) return;
    const doDel = async () => {
      try {
        await api.delete(`/orders/${id}`);
        toast({ title: "Đã xóa đơn", variant: "success" });
        navigate("/orders");
      } catch (e) {
        if (e?.response?.status === 403 && e?.response?.data?.detail === "PIN2_REQUIRED") {
          setPin2RetryAction(() => doDel);
          setPin2Open(true);
          return;
        }
        toast({ title: parseApiError(e) || t("common.error"), variant: "error" });
      }
    };
    await doDel();
  };

  if (loading) return (
    <div className="space-y-4 animate-fade-in">
      <div className="h-8 skeleton-shimmer rounded w-48" />
      <div className="h-48 skeleton-shimmer rounded-xl" />
      <div className="h-64 skeleton-shimmer rounded-xl" />
    </div>
  );
  if (!order) return <div className="text-sm text-ink-muted">{t("common.empty")}</div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="order-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/orders")} data-testid="order-detail-back">
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold">{order.order_code}</h1>
            <p className="text-sm text-ink-muted">{formatDateTime(order.created_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant={order.status} className="text-sm px-3 py-1">{t(`status.${order.status}`)}</Badge>
          {canDuplicate && (
            <Button variant="outline" size="sm" onClick={duplicate} data-testid="order-duplicate">
              <Copy size={14} /> {t("orders.duplicate")}
            </Button>
          )}
          {!["delivered","cancelled"].includes(order.status) && (
            <Button variant="outline" size="sm" onClick={openEdit} data-testid="order-edit">
              <Edit3 size={14} /> Chỉnh sửa
            </Button>
          )}
          {canBillPrint && (
            <>
              <Button variant="outline" size="sm" onClick={() => setPrintMode("80mm")} data-testid="order-print-80mm">
                <Receipt size={14} /> Bill 80mm
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPrintMode("pxk")} data-testid="order-print-pxk">
                <FileText size={14} /> Phiếu XK A4
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPrintMode("pxk-a5")} data-testid="order-print-pxk-a5">
                <FileText size={14} /> Phiếu XK A5
              </Button>
            </>
          )}
          {canDeleteOrder && (
            <Button variant="outline" size="sm" onClick={removeOrder} className="text-red-700 border-red-300 hover:bg-red-50" data-testid="order-delete">
              <XCircle size={14} /> Xóa đơn
            </Button>
          )}
        </div>
      </div>

      <Pin2Prompt
        open={pin2Open}
        onClose={() => { setPin2Open(false); setPin2RetryAction(null); }}
        onVerified={() => {
          setPin2Open(false);
          const fn = pin2RetryAction;
          setPin2RetryAction(null);
          if (typeof fn === "function") fn();
        }}
        action="thao tác trên đơn hàng"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader><CardTitle>{t("orders.items")}</CardTitle></CardHeader>
            <CardContent className="!p-0">
              <table className="w-full text-sm">
                <thead className="bg-cream/40 text-xs uppercase text-ink-muted">
                  <tr>
                    <th className="text-left py-2.5 px-4">{t("common.name")}</th>
                    <th className="text-right py-2.5 px-4">{t("common.price")}</th>
                    <th className="text-right py-2.5 px-4">{t("common.quantity")}</th>
                    <th className="text-right py-2.5 px-4">{t("orders.subtotal")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(order.items || []).map((it, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="py-3 px-4">{it.name}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs">{formatVND(it.price)}</td>
                      <td className="py-3 px-4 text-right">{it.quantity}</td>
                      <td className="py-3 px-4 text-right font-medium">{formatVND(it.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-4 space-y-1.5 border-t border-border">
                <div className="flex justify-between text-sm"><span>{t("orders.subtotal")}</span><span className="font-mono">{formatVND(order.subtotal)}</span></div>
                {order.discount_percent > 0 && (
                  <div className="flex justify-between text-sm text-ink-muted">
                    <span>Giảm {order.discount_percent}%</span>
                    <span className="font-mono">- {formatVND(Math.round(order.subtotal * order.discount_percent / 100))}</span>
                  </div>
                )}
                {order.discount > 0 && (
                  <div className="flex justify-between text-sm text-ink-muted">
                    <span>{t("orders.discount")}</span>
                    <span className="font-mono">- {formatVND(order.discount)}</span>
                  </div>
                )}
                {order.shipping_fee > 0 && (
                  <div className="flex justify-between text-sm text-ink-muted">
                    <span>{t("orders.shipping")}</span>
                    <span className="font-mono">+ {formatVND(order.shipping_fee)}</span>
                  </div>
                )}
                <div className="flex justify-between font-heading pt-2 border-t border-border">
                  <span className="font-semibold">{t("orders.total")}</span>
                  <span className="text-lg font-bold text-bamboo" data-testid="order-detail-total">{formatVND(order.total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>{t("orders.timeline")}</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(order.timeline || []).length === 0 && (
                  <p className="text-sm text-ink-muted">Chưa có lịch sử trạng thái.</p>
                )}
                {(order.timeline || []).map((entry, i) => {
                  const Icon = STATUS_ICONS[entry.status] || Clock;
                  return (
                    <div key={i} className="flex gap-3" data-testid={`timeline-${i}`}>
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 rounded-full bg-bamboo text-white flex items-center justify-center">
                          <Icon size={14} />
                        </div>
                        {i < (order.timeline || []).length - 1 && <div className="w-0.5 flex-1 bg-border my-1" />}
                      </div>
                      <div className="pb-4">
                        <div className="flex items-center gap-2">
                          <Badge variant={entry.status}>{t(`status.${entry.status}`)}</Badge>
                          <span className="text-xs text-ink-muted">{formatDateTime(entry.at)}</span>
                        </div>
                        <div className="text-xs text-ink-muted mt-1">{entry.by}</div>
                        {entry.note && <div className="text-sm text-ink mt-1">{entry.note}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle>{t("orders.customer")}</CardTitle></CardHeader>
            <CardContent className="space-y-1.5 text-sm">
              <div className="font-medium text-base">{order.customer_name}</div>
              {order.customer_phone && <div className="text-ink-muted">{order.customer_phone}</div>}
              {order.customer_address && <div className="text-ink-muted">{order.customer_address}</div>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>{t("orders.payment")}</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-ink-muted">{t("orders.payment")}</span><span>{t(`payment.${order.payment_method}`)}</span></div>
              <div className="flex justify-between"><span className="text-ink-muted">Status</span>
                <Badge variant={order.is_paid ? "delivered" : "preparing"}>
                  {order.is_paid ? t("orders.paid") : t("orders.unpaid")}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {(NEXT_STATUS[order.status] || []).length > 0 && (
            <Card>
              <CardHeader><CardTitle>{t("orders.updateStatus")}</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>{t("orders.statusNote")}</Label>
                  <Textarea value={statusNote} onChange={(e) => setStatusNote(e.target.value)} placeholder={t("orders.statusNote")} />
                </div>
                <div className="flex flex-col gap-2">
                  {(NEXT_STATUS[order.status] || []).map((s) => (
                    <Button
                      key={s}
                      variant={s === "cancelled" ? "outline" : "primary"}
                      onClick={() => updateStatus(s)}
                      className={s === "cancelled" ? "text-red-700 border-red-200 hover:bg-red-50" : ""}
                      data-testid={`order-update-status-${s}`}
                    >
                      → {t(`status.${s}`)}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* ── Modal chỉnh sửa đơn ── */}
      {editOpen && editForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3"
          style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(2px)" }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-amber-50">
              <div>
                <div className="font-bold text-base flex items-center gap-2">
                  <Edit3 size={16} className="text-amber-600" /> Chỉnh sửa đơn {order.order_code}
                </div>
                <div className="text-xs text-ink-muted mt-0.5">Cần mật khẩu cấp 2 để lưu</div>
              </div>
              <button onClick={() => setEditOpen(false)} className="text-ink-muted hover:text-ink text-xl">✕</button>
            </div>

            <div className="overflow-y-auto flex-1 p-5 space-y-5">

              {/* Thông tin khách */}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 font-semibold text-sm text-ink-muted uppercase tracking-wide">Thông tin khách</div>
                <div>
                  <Label>Tên khách</Label>
                  <Input value={editForm.customer_name} onChange={e => setEditForm(f => ({...f, customer_name: e.target.value}))} />
                </div>
                <div>
                  <Label>Số điện thoại</Label>
                  <Input value={editForm.customer_phone} onChange={e => setEditForm(f => ({...f, customer_phone: e.target.value}))} />
                </div>
                <div className="col-span-2">
                  <Label>Địa chỉ</Label>
                  <Input value={editForm.customer_address} onChange={e => setEditForm(f => ({...f, customer_address: e.target.value}))} />
                </div>
              </div>

              {/* Sản phẩm */}
              <div>
                <div className="font-semibold text-sm text-ink-muted uppercase tracking-wide mb-2">Sản phẩm</div>
                <div className="space-y-2">
                  {editForm.items.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2.5 rounded-lg border border-border bg-cream/30">
                      <div className="flex-1 min-w-0">
                        <select
                          className="w-full text-sm border border-border rounded-md px-2 py-1.5 bg-white"
                          value={item.product_id}
                          onChange={e => {
                            const prod = products.find(p => p.product_id === e.target.value);
                            setEditForm(f => ({ ...f, items: f.items.map((it, i) =>
                              i === idx ? { ...it, product_id: e.target.value,
                                name: prod?.name || it.name,
                                price: prod?.price || it.price,
                                unit: prod?.unit || it.unit,
                                subtotal: (prod?.price || it.price) * it.quantity,
                              } : it
                            )}));
                          }}
                        >
                          {products.map(p => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
                        </select>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setEditForm(f => ({...f, items: f.items.map((it,i) =>
                          i===idx ? {...it, quantity: Math.max(1, it.quantity-1), subtotal: it.price*(Math.max(1,it.quantity-1))} : it
                        )}))} className="w-7 h-7 rounded border border-border flex items-center justify-center hover:bg-cream">
                          <Minus size={12} />
                        </button>
                        <input
                          type="number" min={1}
                          value={item.quantity}
                          ref={el => quantityRefs.current[idx] = el}
                          onChange={e => {
                            const val = e.target.value;
                            setEditForm(f => ({...f, items: f.items.map((it,i) =>
                              i===idx ? {...it, quantity: val === "" ? "" : Number(val), subtotal: it.price*(Number(val)||0)} : it
                            )}));
                          }}
                          onKeyDown={e => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              // Thêm sản phẩm mới và focus vào ô số lượng của nó
                              const first = products[0];
                              if (!first) return;
                              setEditForm(f => {
                                const newItems = [...f.items, {
                                  product_id: first.product_id, name: first.name,
                                  price: first.price, quantity: "",
                                  subtotal: 0, unit: first.unit || "",
                                }];
                                setTimeout(() => {
                                  const newIdx = newItems.length - 1;
                                  quantityRefs.current[newIdx]?.focus();
                                }, 50);
                                return {...f, items: newItems};
                              });
                            }
                          }}
                          className="w-14 text-center text-sm border border-border rounded-md px-1 py-1.5"
                        />
                        <button onClick={() => setEditForm(f => ({...f, items: f.items.map((it,i) =>
                          i===idx ? {...it, quantity: it.quantity+1, subtotal: it.price*(it.quantity+1)} : it
                        )}))} className="w-7 h-7 rounded border border-border flex items-center justify-center hover:bg-cream">
                          <Plus size={12} />
                        </button>
                      </div>
                      <div className="text-sm font-mono w-24 text-right">
                        {(Number(item.price) * Number(item.quantity)).toLocaleString("vi-VN")}đ
                      </div>
                      <button onClick={() => setEditForm(f => ({...f, items: f.items.filter((_,i) => i!==idx)}))}
                        className="text-red-400 hover:text-red-600">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const first = products[0];
                      if (!first) return;
                      setEditForm(f => {
                        const newItems = [...f.items, {
                          product_id: first.product_id, name: first.name,
                          price: first.price, quantity: "",
                          subtotal: 0, unit: first.unit || "",
                        }];
                        // focus vào ô số lượng của item mới sau khi render
                        setTimeout(() => {
                          const newIdx = newItems.length - 1;
                          quantityRefs.current[newIdx]?.focus();
                        }, 50);
                        return {...f, items: newItems};
                      });
                    }}
                    className="w-full py-2 border border-dashed border-border rounded-lg text-sm text-ink-muted hover:border-bamboo hover:text-bamboo flex items-center justify-center gap-1"
                  >
                    <Plus size={14} /> Thêm sản phẩm
                  </button>
                </div>
              </div>

              {/* Thanh toán & chiết khấu */}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 font-semibold text-sm text-ink-muted uppercase tracking-wide">Thanh toán</div>
                <div>
                  <Label>Hình thức</Label>
                  <Select value={editForm.payment_method} onChange={e => setEditForm(f => ({...f, payment_method: e.target.value}))}>
                    <option value="cash">Tiền mặt</option>
                    <option value="transfer">Chuyển khoản</option>
                    <option value="card">Thẻ ngân hàng</option>
                    <option value="ewallet">Ví điện tử</option>
                    <option value="debt">Công nợ</option>
                  </Select>
                </div>
                <div>
                  <Label>Phí vận chuyển</Label>
                  <Input type="number" min={0} value={editForm.shipping_fee} onChange={e => setEditForm(f => ({...f, shipping_fee: e.target.value}))} />
                </div>
                <div>
                  <Label>Giảm giá %</Label>
                  <Input type="number" min={0} max={100} value={editForm.discount_percent} onChange={e => setEditForm(f => ({...f, discount_percent: e.target.value}))} />
                </div>
                <div>
                  <Label>Giảm giá cố định</Label>
                  <Input type="number" min={0} value={editForm.discount} onChange={e => setEditForm(f => ({...f, discount: e.target.value}))} />
                </div>
                <div className="col-span-2">
                  <Label>Ghi chú</Label>
                  <Input value={editForm.note} onChange={e => setEditForm(f => ({...f, note: e.target.value}))} />
                </div>
              </div>

              {/* Tổng tiền preview */}
              <div className="rounded-xl bg-bamboo/5 border border-bamboo/20 p-4 flex justify-between items-center">
                <span className="text-sm font-semibold">Tổng tiền mới</span>
                <span className="text-xl font-bold text-bamboo">{editTotal.toLocaleString("vi-VN")}đ</span>
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-border flex gap-2 justify-end bg-gray-50">
              <button onClick={() => setEditOpen(false)}
                className="px-4 py-2 rounded-lg border border-border text-sm text-ink-muted hover:bg-cream">
                Hủy
              </button>
              <button onClick={saveEdit} disabled={saving || editForm.items.length === 0}
                className="px-5 py-2 rounded-lg bg-bamboo text-white text-sm font-semibold hover:bg-bamboo/90 disabled:opacity-50 flex items-center gap-2">
                {saving ? "Đang lưu..." : "💾 Lưu thay đổi"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print preview modal */}
      <PrintPreview
        open={printMode !== null}
        onClose={() => setPrintMode(null)}
        title={printMode === "80mm" ? "Bill 80mm" : printMode === "pxk-a5" ? "Phiếu Xuất Kho A5" : "Phiếu Xuất Kho A4"}
        printId="bill-print-area"
        pdfFilename={`bill-${order.order_code}.pdf`}
        pdfFormat={printMode === "80mm" ? "80mm" : printMode === "pxk-a5" ? "a5" : "a4"}
      >
        {printMode === "80mm" ? (
          <BillThermal order={order} shop={shop} />
        ) : printMode === "pxk-a5" ? (
          <PhieuXuatKho order={order} shop={{ ...shop, pxk_paper_size: "a5" }} />
        ) : (
          <PhieuXuatKho order={order} shop={{ ...shop, pxk_paper_size: "a4" }} />
        )}
      </PrintPreview>
    </div>
  );
}